import {
  AudioWaveform,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  Gamepad2,
  Link2,
  LoaderCircle,
  MonitorSmartphone,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, apiRequest } from "../lib/apiClient";
import { Brand } from "../components/Brand";

type PairingAttempt = {
  attemptId: string;
  code: string;
  browserToken: string;
  expiresAt: number;
};

export function PairingPage({ onPaired }: { onPaired: () => void }) {
  const [attempt, setAttempt] = useState<PairingAttempt | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [status, setStatus] = useState<"creating" | "pending" | "paired" | "error">(
    "creating",
  );
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const browserTokenRef = useRef("");
  const initialLifetimeRef = useRef(1);
  const copiedTimeoutRef = useRef<number | null>(null);

  const createAttempt = useCallback(async () => {
    setStatus("creating");
    setError("");
    setCopied(false);
    try {
      const created = await apiRequest<PairingAttempt>("/api/v1/pairing/attempts", {
        method: "POST",
      });
      browserTokenRef.current = created.browserToken;
      initialLifetimeRef.current = Math.max(
        1,
        Math.ceil((created.expiresAt - Date.now()) / 1000),
      );
      setAttempt(created);
      setStatus("pending");
    } catch (caught) {
      setStatus("error");
      setError(
        caught instanceof Error
          ? caught.message
          : "Impossible de creer un code d'appairage.",
      );
    }
  }, []);

  useEffect(() => {
    void createAttempt();
  }, [createAttempt]);

  useEffect(
    () => () => {
      if (copiedTimeoutRef.current !== null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!attempt || status !== "pending") return;
    let stopped = false;

    const updateRemaining = () => {
      setSecondsRemaining(
        Math.max(0, Math.ceil((attempt.expiresAt - Date.now()) / 1000)),
      );
    };
    updateRemaining();
    const countdown = window.setInterval(updateRemaining, 250);

    const poll = window.setInterval(async () => {
      try {
        const result = await apiRequest<{
          status: "pending" | "paired";
          expiresAt: number;
        }>(`/api/v1/pairing/attempts/${attempt.attemptId}/status`, {
          headers: { Authorization: `Bearer ${browserTokenRef.current}` },
        });
        if (stopped || result.status !== "paired") return;
        setStatus("paired");
        window.setTimeout(onPaired, 450);
      } catch (caught) {
        if (stopped) return;
        if (
          caught instanceof ApiError &&
          ["pairing_expired", "pairing_consumed"].includes(caught.code)
        ) {
          setStatus("error");
          setError("Ce code a expire. Generez-en un nouveau.");
        }
      }
    }, 1_500);

    return () => {
      stopped = true;
      window.clearInterval(countdown);
      window.clearInterval(poll);
    };
  }, [attempt, onPaired, status]);

  const formattedTime = `${Math.floor(secondsRemaining / 60)}:${String(
    secondsRemaining % 60,
  ).padStart(2, "0")}`;
  const expiryProgress = Math.max(
    0,
    Math.min(100, (secondsRemaining / initialLifetimeRef.current) * 100),
  );

  async function copyCode() {
    if (!attempt) return;
    await navigator.clipboard.writeText(attempt.code);
    setCopied(true);
    if (copiedTimeoutRef.current !== null) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1_800);
  }

  return (
    <div className="pairing-page">
      <header className="public-header">
        <Brand />
        <div className="public-header-actions">
          <span className="service-availability">
            <i />
            Service disponible
          </span>
          <nav aria-label="Liens secondaires">
            <a href="/customer">Espace studio</a>
            <a href="/legal/privacy">Confidentialite</a>
          </nav>
        </div>
      </header>

      <main className="pairing-main">
        <section className="pairing-copy">
          <div className="pairing-signal" aria-hidden="true">
            <span><AudioWaveform size={16} /> Canal vocal securise</span>
            <div>
              {Array.from({ length: 12 }, (_, index) => <i key={index} />)}
            </div>
          </div>
          <p className="eyebrow">Compagnon vocal spatial</p>
          <h1>Reliez votre session Roblox.</h1>
          <p className="pairing-lead">
            Vox associe ce navigateur au joueur present dans votre serveur. Le
            code est temporaire et ne permet pas de choisir une autre identite.
          </p>
          <div className="pairing-route" aria-label="Liaison du navigateur a Roblox">
            <span>
              <MonitorSmartphone size={18} />
              <small>Ce navigateur</small>
              <strong>Microphone</strong>
            </span>
            <i aria-hidden="true" />
            <span>
              <Gamepad2 size={18} />
              <small>Votre session</small>
              <strong>Roblox</strong>
            </span>
          </div>
          <div className="trust-row">
            <span><ShieldCheck size={16} /> Code a usage unique</span>
            <span><Clock3 size={16} /> Expiration automatique</span>
          </div>
        </section>

        <section className="pairing-panel" aria-live="polite">
          <div className="pairing-panel-header">
            <span className="step-index">01</span>
            <div>
              <p className="eyebrow">Connexion a votre joueur</p>
              <h2>Utilisez ce code dans Roblox</h2>
            </div>
            <span className="pairing-channel">Canal prive</span>
          </div>

          {status === "creating" ? (
            <div className="pairing-loading">
              <LoaderCircle className="spin" />
              Creation d'un code securise...
            </div>
          ) : null}

          {attempt && status !== "creating" ? (
            <>
              <div className="pairing-code-row">
                <strong className="pairing-code">{attempt.code}</strong>
                <button
                  className="secondary-button icon-label-button"
                  type="button"
                  aria-label="Copier le code d'appairage"
                  onClick={() => void copyCode()}
                >
                  {copied ? <Check size={17} /> : <Copy size={17} />}
                  {copied ? "Copie" : "Copier"}
                </button>
              </div>
              <div className="pairing-expiry">
                <div>
                  <span>Temps restant</span>
                  <strong>{formattedTime}</strong>
                </div>
                <span className="pairing-expiry-track" aria-hidden="true">
                  <i style={{ width: `${expiryProgress}%` }} />
                </span>
              </div>
            </>
          ) : null}

          <ol className="pairing-steps">
            <li>
              <span>1</span>
              <div>
                <strong>Ouvrez Vox dans Roblox</strong>
                <small>Depuis votre partie, affichez le panneau d'appairage.</small>
              </div>
              <kbd>F8</kbd>
            </li>
            <li>
              <span>2</span>
              <div>
                <strong>Saisissez le code</strong>
                <small>Le serveur verifie directement votre identite Roblox.</small>
              </div>
            </li>
            <li>
              <span>3</span>
              <div>
                <strong>Autorisez le microphone</strong>
                <small>Cette page ouvrira la salle vocale automatiquement.</small>
              </div>
            </li>
          </ol>

          <div className={`pairing-state pairing-state-${status}`}>
            {status === "pending" ? (
              <>
                <span className="pulse-dot" />
                En attente de Roblox
              </>
            ) : null}
            {status === "paired" ? (
              <>
                <CheckCircle2 size={17} />
                Identite verifiee. Ouverture de la salle...
              </>
            ) : null}
            {status === "error" ? (
              <>
                <span>{error}</span>
                <button className="text-button" onClick={() => void createAttempt()}>
                  <RefreshCw size={15} /> Nouveau code
                </button>
              </>
            ) : null}
          </div>
        </section>

        <aside className="pairing-notice">
          <Link2 size={18} />
          <div>
            <strong>Service tiers independant</strong>
            <p>
              Vox utilise votre microphone dans ce navigateur apres votre accord.
              Aucun audio n'est enregistre par defaut.
            </p>
            <a href="/legal/privacy">
              Consulter le traitement des donnees <ExternalLink size={13} />
            </a>
          </div>
        </aside>
      </main>

      <footer className="public-footer">
        <span>Vox est un service independant, non affilie a Roblox Corporation.</span>
        <div>
          <a href="/legal/terms">Conditions</a>
          <a href="/legal/privacy">Confidentialite</a>
          <a href="/legal/moderation">Moderation</a>
        </div>
      </footer>
    </div>
  );
}
